// ============================================================================
// BOQ — Library hook (items + item_versions)
// ============================================================================
// Lists items with their versions; creates items (with a first version) and
// new versions on existing items. Updates a version's content fields.
//
// RLS: shared read, admin/supervisor write on both tables.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { canonicalUnit } from '../utils/measureUnits';
import { buildClassificationMaps, fetchAllRows } from '../utils/libraryClassification';
import { buildSubtradeLinks } from '../utils/tradeGroupSubtrades';
import { generateItemCode, itemCodeFitsClassification, isPurgeCode } from '../utils/itemCodeGenerator';
import {
  DESCRIPTION_PARTS, DESCRIPTION_PART_COLUMNS, composeDescription, validateDescriptionParts,
  descriptionPartsPayload, descriptionPartsFromRow,
} from '../utils/descriptionParts';

const ITEM_COLUMNS = 'item_code, trade, subtrade, sequence, status, created_at, updated_at';
// Full version shape — used by inserts (so freshly created rows carry their
// specs) and by the lazy per-trade detail loader.
const VERSION_COLUMNS =
  'id, item_code, version_letter, full_code, short_description, long_spec_general, ' +
  'long_spec_products, long_spec_execution, unit, measurement_ref, status, change_reason, ' +
  `source_document_id, merged_from_code, created_at, updated_at, ${DESCRIPTION_PART_COLUMNS}`;
// Slim shape for the initial bulk list: everything the library table, search
// and trade selector need EXCEPT the three heavy long_spec_* text columns,
// which are fetched on demand per trade via loadGroupDetail().
const VERSION_LIST_COLUMNS =
  'id, item_code, version_letter, full_code, short_description, unit, measurement_ref, ' +
  `status, change_reason, source_document_id, merged_from_code, created_at, updated_at, ${DESCRIPTION_PART_COLUMNS}`;
const VERSION_SPEC_COLUMNS = 'id, long_spec_general, long_spec_products, long_spec_execution';

// TRADE.SUBTRADE.SEQUENCE, or a Purge code (OO.OO.0001), whose middle segment
// is two letters. Same as items_item_code_format_chk.
const ITEM_CODE_RE = /^([A-Z]{2})\.([A-Z]{3}|OO)\.(\d{4})$/;

const mapItem = (i) => ({
  itemCode: i.item_code,
  trade: i.trade,
  subtrade: i.subtrade,
  sequence: i.sequence,
  status: i.status,
  createdAt: i.created_at,
  updatedAt: i.updated_at,
});

const mapVersion = (v) => ({
  id: v.id,
  itemCode: v.item_code,
  versionLetter: v.version_letter,
  fullCode: v.full_code,
  shortDescription: v.short_description,
  longSpecGeneral: v.long_spec_general,
  longSpecProducts: v.long_spec_products,
  longSpecExecution: v.long_spec_execution,
  unit: v.unit,
  measurementRef: v.measurement_ref,
  status: v.status,
  changeReason: v.change_reason,
  sourceDocumentId: v.source_document_id,
  // The item code this version carried before a merge moved it here; null when
  // it has always belonged to this item.
  mergedFromCode: v.merged_from_code || null,
  createdAt: v.created_at,
  updatedAt: v.updated_at,
  // The five short description parts (Location, Type, Material, Thickness,
  // Size). All null on a version saved before they existed, which keeps its
  // free text shortDescription.
  ...descriptionPartsFromRow(v),
});

// The short description a save writes: the five parts, and the one line they
// compose to. boq.compose_short_description() rebuilds the line from the parts
// on the way in, so the two always agree; sending it too keeps the optimistic
// row in state right before the round trip returns.
const descriptionPayload = (input) => ({
  short_description: composeDescription(input),
  ...descriptionPartsPayload(input),
});
const touchesDescriptionParts = (patch) => DESCRIPTION_PARTS.some((p) => patch[p.key] !== undefined);

export const parseItemCode = (raw) => {
  const code = (raw || '').trim().toUpperCase();
  const match = ITEM_CODE_RE.exec(code);
  if (!match) return null;
  return {
    code,
    trade: match[1],
    subtrade: match[2],
    sequence: parseInt(match[3], 10),
  };
};

// Codes of archived items. The library hides them, but each still owns its
// code, so the generator must not fill a gap with one.
const fetchArchivedCodes = async () => {
  const { data, error: aErr } = await supabase.schema('boq').from('items').select('item_code').eq('status', 'deprecated');
  if (aErr) {
    console.warn('[useBoqLibrary] archived item codes unavailable', aErr.message);
    return [];
  }
  return (data || []).map((r) => parseItemCode(r.item_code)).filter(Boolean);
};

export const useBoqLibrary = ({ enabled = true } = {}) => {
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [tradeGroups, setTradeGroups] = useState([]);            // sorted trade_group nodes
  const [tradeGroupByItem, setTradeGroupByItem] = useState(() => new Map()); // itemCode → node
  // itemCode → canonical brio_trade chapter label. Resolved from whichever
  // taxonomy an item is classified in (brio_trade subtrade → its parent chapter,
  // or trade_group → chapter via the crosswalk), so bills tagged in either
  // vocabulary roll up to the same 11 chapters.
  const [chapterByItem, setChapterByItem] = useState(() => new Map());
  // itemCode → canonical brio_trade SUB-TRADE label (the child node it's tagged
  // with), so tender/library bills show the same sub-trades as SD.
  const [subtradeByItem, setSubtradeByItem] = useState(() => new Map());
  const [brioSubtradeNodeByItem, setBrioSubtradeNodeByItem] = useState(() => new Map()); // itemCode → subtrade node id
  const [brioSubtrades, setBrioSubtrades] = useState([]);        // [{id,code,label,chapterLabel,...}] for pickers
  // trade_type taxonomy: the third picker on an item row, filtered to the
  // types under the item's sub-trade. Descriptive only — nothing derives an
  // item code from it yet.
  const [tradeTypes, setTradeTypes] = useState([]);
  // Map trade group id → Set of sub-trade ids ticked for it on the Taxonomies
  // page. New item and the Edit modal narrow the sub-trade dropdown by it; a
  // trade group with nothing ticked offers every sub-trade.
  const [subtradeLinks, setSubtradeLinks] = useState(() => new Map());
  const [tradeTypeNodeByItem, setTradeTypeNodeByItem] = useState(() => new Map());
  const [rateAggByItem, setRateAggByItem] = useState(() => new Map()); // itemCode → { count, latestVal, latestDate }
  const [archivedCodes, setArchivedCodes] = useState([]);   // archived items' codes, for gap filling
  // True for admin / supervisor. Library INSERT is open to every signed-in user
  // (so anyone can duplicate an item, or add one), but UPDATE and DELETE are
  // not. Controls that re-tag, edit or reconcile an existing item read this:
  // without it their DELETE would silently affect zero rows under RLS while the
  // paired INSERT succeeded, leaving an item wearing two trade-group tags.
  const [canWrite, setCanWrite] = useState(false);
  // boq.can_delete_item_version(): admins, supervisors and named curators. Shows
  // Delete on a version made with + New version in the Edit modal.
  const [canDeleteVersion, setCanDeleteVersion] = useState(false);
  // itemCode → ISO timestamp of the item's latest change anywhere: item row,
  // versions, rates, classification tags. From boq.library_item_modified_at(),
  // which reads the audit log so tag changes and deletions count too.
  const [modifiedAtByItem, setModifiedAtByItem] = useState(() => new Map());
  const [detailLoading, setDetailLoading] = useState(false);     // a per-trade detail fetch is in flight
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Latest versions list, read by the (stable) lazy loader without making it a
  // dependency — so loadGroupDetail keeps a constant identity across renders.
  const versionsRef = useRef([]);
  const detailLoadedRef = useRef(new Set());                     // item codes whose specs + rates are loaded

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    detailLoadedRef.current = new Set();
    setRateAggByItem(new Map());
    try {
      // item_versions and item_version_classifications exceed PostgREST's 1000-row
      // cap, so they're paginated; the taxonomy tables are small (single page).
      const [itemsData, versionsData, classData, taxRes, nodesRes, linksRes] = await Promise.all([
        // Deprecated items are excluded: restoring a library archive deprecates
        // (rather than deletes) any item a project bill still points at, and
        // that item has no business showing in the browser afterwards.
        fetchAllRows(() => supabase.schema('boq').from('items').select(ITEM_COLUMNS).neq('status', 'deprecated').order('item_code')),
        fetchAllRows(() => supabase.schema('boq').from('item_versions').select(VERSION_LIST_COLUMNS).order('full_code')),
        // Ordered because fetchAllRows pages with .range(): without a stable sort
        // Postgres may return a row on two pages, or on none.
        fetchAllRows(() => supabase.schema('boq').from('item_version_classifications')
          .select('item_version_id, classification_node_id').order('item_version_id').order('classification_node_id')),
        supabase.schema('boq').from('classification_taxonomies').select('id, code'),
        supabase.schema('boq').from('classification_nodes').select('id, taxonomy_id, code, label, sort_order, parent_id, maps_to_node_id, short_code, series_start, series_end').order('sort_order'),
        supabase.schema('boq').from('subtrade_trade_groups').select('subtrade_node_id, trade_group_node_id'),
      ]);
      if (taxRes.error) throw taxRes.error;
      if (nodesRes.error) throw nodesRes.error;
      if (linksRes.error) throw linksRes.error;
      setSubtradeLinks(buildSubtradeLinks(linksRes.data));

      setItems(itemsData.map(mapItem));
      setVersions(versionsData.map(mapVersion));
      setArchivedCodes(await fetchArchivedCodes());

      // Classification maps (trade group / chapter / sub-trade) come from a
      // shared builder so the archive viewer classifies an archive by exactly
      // the same rules — see utils/libraryClassification.js.
      const maps = buildClassificationMaps({
        versionRows: versionsData,
        classificationRows: classData,
        taxonomyRows: taxRes.data,
        nodeRows: nodesRes.data,
      });
      // Surface any trade in use that doesn't roll up to a chapter, so the gap is
      // visible during development (the DB view boq.trade_group_chapter_gaps is
      // the durable check).
      if (maps.unmappedTradeGroups.size > 0 && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[useBoqLibrary] trade_group(s) with no brio_trade chapter link:', [...maps.unmappedTradeGroups].join(', '));
      }
      setTradeGroups(maps.tradeGroups);
      setTradeGroupByItem(maps.tradeGroupByItem);
      setChapterByItem(maps.chapterByItem);
      setSubtradeByItem(maps.subtradeByItem);
      setBrioSubtradeNodeByItem(maps.brioSubtradeNodeByItem);
      setBrioSubtrades(maps.brioSubtrades);
      setTradeTypes(maps.tradeTypes);
      setTradeTypeNodeByItem(maps.tradeTypeNodeByItem);
    } catch (err) {
      console.error('[useBoqLibrary] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'library' }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-read the per-item "Date modified". Cheap (one row per item), so it runs
  // after the first load and again, debounced, whenever the item list, the
  // versions or any tag map changes — every library write lands in one of
  // those. A failure keeps what we had; the page falls back to the item and
  // version timestamps it already holds.
  const refreshModifiedAt = useCallback(async () => {
    const { data, error: rpcErr } = await supabase.schema('boq').rpc('library_item_modified_at');
    if (rpcErr) {
      console.warn('[useBoqLibrary] library_item_modified_at failed', rpcErr);
      return;
    }
    setModifiedAtByItem(new Map((data || []).map((r) => [r.item_code, r.modified_at])));
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(refreshModifiedAt, 400);
    return () => clearTimeout(t);
  }, [loading, items, versions, tradeGroupByItem, brioSubtradeNodeByItem, tradeTypeNodeByItem, refreshModifiedAt]);

  // Re-read only the taxonomy rows the create and duplicate pickers draw from:
  // trade groups, sub-trades with their ranges, trade types with their series.
  // fetchAll reads them once, when the page loads, so a range or trade type set
  // on the Taxonomies page afterwards would otherwise never reach the item code
  // generator. Two small tables, and quiet: the item list is left as it is.
  const refreshPickers = useCallback(async () => {
    try {
      const [taxRes, nodesRes, archived, linksRes] = await Promise.all([
        supabase.schema('boq').from('classification_taxonomies').select('id, code'),
        supabase.schema('boq').from('classification_nodes').select('id, taxonomy_id, code, label, sort_order, parent_id, maps_to_node_id, short_code, series_start, series_end').order('sort_order'),
        fetchArchivedCodes(),
        supabase.schema('boq').from('subtrade_trade_groups').select('subtrade_node_id, trade_group_node_id'),
      ]);
      if (taxRes.error) throw taxRes.error;
      if (nodesRes.error) throw nodesRes.error;
      if (linksRes.error) throw linksRes.error;
      setSubtradeLinks(buildSubtradeLinks(linksRes.data));
      // An item may have been archived since the page loaded.
      setArchivedCodes(archived);
      const maps = buildClassificationMaps({
        versionRows: [], classificationRows: [], taxonomyRows: taxRes.data, nodeRows: nodesRes.data,
      });
      setTradeGroups(maps.tradeGroups);
      setBrioSubtrades(maps.brioSubtrades);
      setTradeTypes(maps.tradeTypes);
      return { success: true };
    } catch (err) {
      // The pickers keep what they had; the modal still works from that.
      console.error('[useBoqLibrary] refreshPickers failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'fetch', entity: 'taxonomies' }) };
    }
  }, []);

  // Create a new item plus its first (implicit) version in a single call.
  const createItemWithFirstVersion = useCallback(async (input) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to create a library item.' };
      }
      const parsed = parseItemCode(input.itemCode);
      if (!parsed) {
        return { success: false, error: 'Item code must be TRADE.SUBTRADE.SEQUENCE — e.g. MA.EXT.0014.' };
      }
      const partsError = validateDescriptionParts(input);
      if (partsError) return { success: false, error: partsError };
      if (!input.unit?.trim()) {
        return { success: false, error: 'Unit is required.' };
      }
      // Enforce a canonical sub-trade so the item is interlinked across SD and
      // tender bills from the moment it's created (no silent drift).
      const sub = brioSubtrades.find((s) => s.id === input.brioSubtradeNodeId);
      if (!sub) {
        return { success: false, error: 'Pick a chapter / sub-trade — this keeps the item lined up across SD and tender bills.' };
      }

      // INSERT items row (idempotent — fail if exists)
      const itemPayload = {
        item_code: parsed.code,
        trade: parsed.trade,
        subtrade: parsed.subtrade,
        sequence: parsed.sequence,
        status: input.status || 'active',
      };
      const { data: itemRow, error: itemErr } = await supabase
        .schema('boq')
        .from('items')
        .insert(itemPayload)
        .select(ITEM_COLUMNS)
        .single();
      if (itemErr) throw itemErr;

      // INSERT implicit first version (version_letter NULL — full_code === item_code)
      const versionPayload = {
        item_code: parsed.code,
        version_letter: null,
        ...descriptionPayload(input),
        long_spec_general: input.longSpecGeneral?.trim() || null,
        long_spec_products: input.longSpecProducts?.trim() || null,
        long_spec_execution: input.longSpecExecution?.trim() || null,
        unit: canonicalUnit(input.unit),
        measurement_ref: input.measurementRef?.trim() || null,
        status: 'active',
      };
      const { data: versionRow, error: versionErr } = await supabase
        .schema('boq')
        .from('item_versions')
        .insert(versionPayload)
        .select(VERSION_COLUMNS)
        .single();
      if (versionErr) throw versionErr;

      // Tag the new version with every classification the caller supplied. The
      // sub-trade is mandatory; the trade group and trade type ride along when
      // the create modal passed them, which it always does now — they are what
      // the item code was generated from, so the tags have to agree with it.
      const tagIds = [sub.id, input.tradeGroupNodeId, input.tradeTypeNodeId].filter(Boolean);
      const { error: clsErr } = await supabase
        .schema('boq')
        .from('item_version_classifications')
        .insert(tagIds.map((id) => ({ item_version_id: versionRow.id, classification_node_id: id })));
      if (clsErr) throw clsErr;

      setItems((prev) => [...prev, mapItem(itemRow)].sort((a, b) => a.itemCode.localeCompare(b.itemCode)));
      setVersions((prev) => [...prev, mapVersion(versionRow)].sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
      setChapterByItem((prev) => new Map(prev).set(parsed.code, sub.chapterLabel));
      setSubtradeByItem((prev) => new Map(prev).set(parsed.code, sub.label));
      setBrioSubtradeNodeByItem((prev) => new Map(prev).set(parsed.code, sub.id));
      if (input.tradeGroupNodeId) {
        const tg = tradeGroups.find((n) => n.id === input.tradeGroupNodeId);
        if (tg) setTradeGroupByItem((prev) => new Map(prev).set(parsed.code, tg));
      }
      if (input.tradeTypeNodeId) {
        setTradeTypeNodeByItem((prev) => new Map(prev).set(parsed.code, input.tradeTypeNodeId));
      }
      return { success: true, item: mapItem(itemRow), version: mapVersion(versionRow) };
    } catch (err) {
      console.error('[useBoqLibrary] createItem failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'library item' }) };
    }
  }, [brioSubtrades, tradeGroups]);

  // The code a new item would take, given the three classifications. Returns
  // { code } or { error } naming what is missing — the modal shows either
  // verbatim, so a curator is told which taxonomy row to go and fix.
  // A sub-trade with no trade types under it numbers from its own range.
  const nextItemCode = useCallback(({ tradeGroupNodeId, brioSubtradeNodeId, tradeTypeNodeId }) => (
    generateItemCode({
      tradeGroup: tradeGroups.find((n) => n.id === tradeGroupNodeId) || null,
      subtrade: brioSubtrades.find((n) => n.id === brioSubtradeNodeId) || null,
      tradeType: tradeTypes.find((n) => n.id === tradeTypeNodeId) || null,
      subtradeHasTypes: tradeTypes.some((t) => t.anchorId && String(t.anchorId) === String(brioSubtradeNodeId)),
      items: archivedCodes.length ? [...items, ...archivedCodes] : items,
    })
  ), [tradeGroups, brioSubtrades, tradeTypes, items, archivedCodes]);

  // The code an EXISTING item would carry under a new classification. Returns
  // { code, renumber } or { error }. The current code is kept when it already
  // reads as the new classification (right prefix, number inside the band), so
  // an item is renumbered only when its old code would misdescribe it.
  const proposeItemCode = useCallback((itemCode, sel) => {
    const tradeGroup = tradeGroups.find((n) => n.id === sel.tradeGroupNodeId) || null;
    const subtrade = brioSubtrades.find((n) => n.id === sel.brioSubtradeNodeId) || null;
    const tradeType = tradeTypes.find((n) => n.id === sel.tradeTypeNodeId) || null;
    const subtradeHasTypes = tradeTypes.some((t) => t.anchorId && String(t.anchorId) === String(sel.brioSubtradeNodeId));
    if (itemCodeFitsClassification({ itemCode, tradeGroup, subtrade, tradeType, subtradeHasTypes })) {
      return { code: itemCode, renumber: false };
    }
    const res = nextItemCode(sel);
    return res.code ? { code: res.code, renumber: true } : res;
  }, [tradeGroups, brioSubtrades, tradeTypes, nextItemCode]);

  // The code a duplicate of `itemCode` would take: the source's own trade
  // group and sub-trade, and the next number in the chosen trade type's series
  // (the source's own type unless the modal picked another under the same
  // sub-trade). Pass tradeTypeNodeId as '' to mean "no type chosen".
  const nextDuplicateCode = useCallback((itemCode, tradeTypeNodeId) => nextItemCode({
    tradeGroupNodeId: tradeGroupByItem.get(itemCode)?.id,
    brioSubtradeNodeId: brioSubtradeNodeByItem.get(itemCode),
    tradeTypeNodeId: tradeTypeNodeId === undefined ? tradeTypeNodeByItem.get(itemCode) : (tradeTypeNodeId || null),
  }), [nextItemCode, tradeGroupByItem, brioSubtradeNodeByItem, tradeTypeNodeByItem]);

  // Copy an existing item to a new item code. Everything that describes the
  // item comes across — every live version with its letter, wording, unit,
  // measurement reference and three-part spec, plus the classification tags
  // (trade group, chapter / sub-trade, sub-trade category). Two things do not:
  // deprecated versions (retired wordings are history of the original, not of
  // the copy) and `boq.rates` — a duplicate starts with no rate history, so a
  // price observed against the source is never silently attributed to it.
  //
  // versionId copies that one version only; it becomes the copy's base version
  // (no letter). tradeTypeNodeId replaces the source's trade type tag on every
  // copied version, so the copy is filed under the series its code came from.
  const duplicateItem = useCallback(async ({ sourceItemCode, itemCode, versionId = null, tradeTypeNodeId }) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to duplicate a library item.' };
      }
      const parsed = parseItemCode(itemCode);
      if (!parsed) {
        return { success: false, error: 'Item code must be TRADE.SUBTRADE.SEQUENCE — e.g. MA.EXT.0014.' };
      }
      if (parsed.code === sourceItemCode) {
        return { success: false, error: 'Give the copy a different item code.' };
      }
      const { data: clash, error: clashErr } = await supabase
        .schema('boq').from('items').select('item_code').eq('item_code', parsed.code).maybeSingle();
      if (clashErr) throw clashErr;
      if (clash) {
        return { success: false, error: `${parsed.code} already exists in the library — pick another code.` };
      }

      // Read the source from the database rather than from state: the long_spec_*
      // columns are loaded lazily per trade, so state may hold a slim row.
      const { data: srcVersions, error: srcErr } = await supabase
        .schema('boq').from('item_versions').select(VERSION_COLUMNS)
        .eq('item_code', sourceItemCode).order('version_letter', { nullsFirst: true });
      if (srcErr) throw srcErr;
      const keep = versionId
        ? (srcVersions || []).filter((v) => v.id === versionId)
        : (srcVersions || []).filter((v) => v.status !== 'deprecated');
      if (keep.length === 0) {
        return { success: false, error: versionId ? 'That version no longer exists.' : 'This item has no live version to duplicate.' };
      }
      // One version copied on its own becomes the copy's base version, active.
      const single = !!versionId;

      const srcItem = items.find((i) => i.itemCode === sourceItemCode);
      const { data: itemRow, error: itemErr } = await supabase
        .schema('boq').from('items')
        .insert({
          item_code: parsed.code,
          trade: parsed.trade,
          subtrade: parsed.subtrade,
          sequence: parsed.sequence,
          status: srcItem?.status || 'active',
        })
        .select(ITEM_COLUMNS)
        .single();
      if (itemErr) throw itemErr;

      const { data: newVersions, error: verErr } = await supabase
        .schema('boq').from('item_versions')
        .insert(keep.map((v) => ({
          item_code: parsed.code,
          version_letter: single ? null : v.version_letter,
          short_description: v.short_description,
          ...DESCRIPTION_PARTS.reduce((acc, p) => ({ ...acc, [p.column]: v[p.column] ?? null }), {}),
          long_spec_general: v.long_spec_general,
          long_spec_products: v.long_spec_products,
          long_spec_execution: v.long_spec_execution,
          unit: v.unit,
          measurement_ref: v.measurement_ref,
          status: single ? 'active' : v.status,
          change_reason: v.change_reason,
          source_document_id: v.source_document_id,
        })))
        .select(VERSION_COLUMNS);
      if (verErr) throw verErr;

      // Carry the tags across, matching source version to copy by letter.
      const { data: srcTags, error: tagErr } = await supabase
        .schema('boq').from('item_version_classifications')
        .select('item_version_id, classification_node_id')
        .in('item_version_id', keep.map((v) => v.id));
      if (tagErr) throw tagErr;
      const newIdByLetter = new Map((newVersions || []).map((v) => [v.version_letter || '', v.id]));
      const letterBySrcId = new Map(keep.map((v) => [v.id, single ? '' : (v.version_letter || '')]));
      // A trade type picked in the modal replaces the source's on every copy.
      const overrideType = tradeTypeNodeId !== undefined;
      const typeIds = new Set(tradeTypes.map((t) => t.id));
      const tagRows = (srcTags || [])
        .filter((t) => !(overrideType && typeIds.has(t.classification_node_id)))
        .map((t) => ({
          item_version_id: newIdByLetter.get(letterBySrcId.get(t.item_version_id)),
          classification_node_id: t.classification_node_id,
        }))
        .filter((r) => r.item_version_id);
      if (overrideType && tradeTypeNodeId) {
        (newVersions || []).forEach((v) => tagRows.push({ item_version_id: v.id, classification_node_id: tradeTypeNodeId }));
      }
      if (tagRows.length) {
        const { error: insTagErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .upsert(tagRows, { onConflict: 'item_version_id,classification_node_id', ignoreDuplicates: true });
        if (insTagErr) throw insTagErr;
      }

      const mappedVersions = (newVersions || []).map(mapVersion);
      setItems((prev) => [...prev, mapItem(itemRow)].sort((a, b) => a.itemCode.localeCompare(b.itemCode)));
      setVersions((prev) => [...prev, ...mappedVersions].sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
      const carry = (setter, value) => setter((prev) => {
        if (value == null) return prev;
        return new Map(prev).set(parsed.code, value);
      });
      carry(setTradeGroupByItem, tradeGroupByItem.get(sourceItemCode));
      carry(setChapterByItem, chapterByItem.get(sourceItemCode));
      carry(setSubtradeByItem, subtradeByItem.get(sourceItemCode));
      carry(setBrioSubtradeNodeByItem, brioSubtradeNodeByItem.get(sourceItemCode));
      carry(setTradeTypeNodeByItem, overrideType ? (tradeTypeNodeId || null) : tradeTypeNodeByItem.get(sourceItemCode));
      // The copy is fully loaded (specs included) and deliberately rate-less —
      // seed the aggregate so the row reads "no rates" instead of "not loaded".
      detailLoadedRef.current.add(parsed.code);
      setRateAggByItem((prev) => new Map(prev).set(parsed.code, { count: 0, latestDate: '', latestVal: null }));

      return { success: true, item: mapItem(itemRow), versionCount: mappedVersions.length };
    } catch (err) {
      console.error('[useBoqLibrary] duplicateItem failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'library item' }) };
    }
  }, [items, tradeTypes, tradeGroupByItem, chapterByItem, subtradeByItem, brioSubtradeNodeByItem, tradeTypeNodeByItem]);

  // Make one version the item's active version; every other active version of
  // the item is archived (status 'deprecated'). Candidates are left alone.
  // boq.set_active_version() does it in one transaction.
  const setActiveVersion = useCallback(async (versionId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to change the active version.' };
      }
      const { error: rpcErr } = await supabase.schema('boq').rpc('set_active_version', { p_version_id: versionId });
      if (rpcErr) throw rpcErr;
      setVersions((prev) => {
        const target = prev.find((v) => v.id === versionId);
        if (!target) return prev;
        return prev.map((v) => {
          if (v.id === versionId) return { ...v, status: 'active' };
          if (v.itemCode === target.itemCode && v.status === 'active') return { ...v, status: 'deprecated' };
          return v;
        });
      });
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] setActiveVersion failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'item version' }) };
    }
  }, []);

  // Merge absorbCode into keepCode: absorbCode's versions move under keepCode
  // (next free letters, archived, keepCode's classification) and absorbCode is
  // retired. See boq.merge_library_items(). Versions move across items and
  // their tags are rewritten, so the library is re-read rather than patched.
  const mergeItems = useCallback(async (keepCode, absorbCode) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to merge library items.' };
      }
      const { data, error: rpcErr } = await supabase
        .schema('boq').rpc('merge_library_items', { p_keep_code: keepCode, p_absorb_code: absorbCode });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true, moved: data };
    } catch (err) {
      console.error('[useBoqLibrary] mergeItems failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'library item' }) };
    }
  }, [fetchAll]);

  // Reverse a merge: the versions of itemCode that came from fromCode go back
  // to fromCode with their old letters, status and tags, and fromCode's item
  // row comes back. boq.unmerge_library_items() reads all of that from the
  // audit log. Like merge, the library is re-read rather than patched.
  const unmergeItems = useCallback(async (itemCode, fromCode) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to unmerge library items.' };
      }
      const { data, error: rpcErr } = await supabase
        .schema('boq').rpc('unmerge_library_items', { p_item_code: itemCode, p_from_code: fromCode });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true, restored: data };
    } catch (err) {
      console.error('[useBoqLibrary] unmergeItems failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'library item' }) };
    }
  }, [fetchAll]);

  // Split: each chosen version of itemCode becomes its own item, numbered from
  // its trade type's series; the earliest live version keeps the code. Read the
  // candidates, preview with dryRun (the same path, rolled back, so the codes
  // shown are the codes the real run mints), then apply. plan is
  // [{ versionId, tradeTypeId }]; a trade type, when given, replaces the
  // version's own before it is numbered. All or nothing: any row with a
  // problem means nothing was written. See boq.split_library_item().
  const loadSplitCandidates = useCallback(async (itemCode) => {
    try {
      const { data, error: rpcErr } = await supabase
        .schema('boq').rpc('split_candidates', { p_item_code: itemCode });
      if (rpcErr) throw rpcErr;
      return {
        success: true,
        versions: (data || []).map((r) => ({
          id: r.version_id,
          fullCode: r.full_code,
          shortDescription: r.short_description,
          unit: r.unit,
          status: r.status,
          rateCount: r.rate_count,
          keepsCode: r.keeps_code,
          subtradeId: r.subtrade_id,
          subtradeLabel: r.subtrade_label,
          tradeTypeId: r.trade_type_id,
          bandProblem: r.band_problem,
          // Project BOQ lines, FF&E schedules and issued bill lines using it:
          // a combined-in version with any of these is archived, not deleted.
          usageCount: r.usage_count || 0,
        })),
      };
    } catch (err) {
      console.error('[useBoqLibrary] loadSplitCandidates failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'load', entity: 'item versions' }) };
    }
  }, []);

  const splitItem = useCallback(async (itemCode, plan, { dryRun = true } = {}) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to split library items.' };
      }
      const { data, error: rpcErr } = await supabase.schema('boq').rpc('split_library_item', {
        p_item_code: itemCode,
        p_plan: plan.map((p) => ({ version_id: p.versionId, trade_type_id: p.tradeTypeId || null })),
        p_dry_run: dryRun,
      });
      if (rpcErr) throw rpcErr;
      const rows = (data || []).map((r) => ({ versionId: r.version_id, newCode: r.new_code, problem: r.problem }));
      const ok = rows.every((r) => !r.problem);
      if (!dryRun && ok) await fetchAll();
      return { success: true, ok, rows };
    } catch (err) {
      console.error('[useBoqLibrary] splitItem failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'library item' }) };
    }
  }, [fetchAll]);

  // Combine: the ticked versions of itemCode merge into the kept one. Their
  // rates move onto it and they are deleted, or archived when a project BOQ or
  // an issued bill still uses them. See boq.combine_library_versions().
  const combineVersions = useCallback(async (itemCode, versionIds, keepVersionId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to combine versions.' };
      }
      const { data, error: rpcErr } = await supabase.schema('boq')
        .rpc('combine_library_versions', {
          p_item_code: itemCode, p_version_ids: versionIds, p_keep_version_id: keepVersionId,
        });
      if (rpcErr) throw rpcErr;
      await fetchAll();
      return { success: true, moved: data?.moved ?? 0, deleted: data?.deleted ?? 0, archived: data?.archived ?? 0 };
    } catch (err) {
      console.error('[useBoqLibrary] combineVersions failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'library item' }) };
    }
  }, [fetchAll]);

  // Hard-delete a library item and its versions. Refused unless nothing points
  // at them — the rule is "no rates", but boq.delete_library_item() also checks
  // project_items, bill_version_lines and ffe_schedules, because the last two
  // have no enforcing FK and a delete would quietly orphan an issued bill
  // snapshot rather than raising. The check and the delete happen in one
  // statement server-side so they cannot race.
  const deleteItem = useCallback(async (itemCode) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to delete a library item.' };
      }
      const { error: rpcErr } = await supabase
        .schema('boq').rpc('delete_library_item', { p_item_code: itemCode });
      if (rpcErr) throw rpcErr;

      const goneIds = new Set(versions.filter((v) => v.itemCode === itemCode).map((v) => v.id));
      setItems((prev) => prev.filter((i) => i.itemCode !== itemCode));
      setVersions((prev) => prev.filter((v) => !goneIds.has(v.id)));
      const drop = (setter) => setter((prev) => {
        if (!prev.has(itemCode)) return prev;
        const next = new Map(prev); next.delete(itemCode); return next;
      });
      drop(setTradeGroupByItem);
      drop(setChapterByItem);
      drop(setSubtradeByItem);
      drop(setBrioSubtradeNodeByItem);
      drop(setTradeTypeNodeByItem);
      drop(setRateAggByItem);
      detailLoadedRef.current.delete(itemCode);
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] deleteItem failed', err);
      // The function raises a sentence meant for a human — surface it as-is
      // rather than letting formatSupabaseError reduce it to a generic message.
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'delete', entity: 'library item' }) };
    }
  }, [versions]);

  // Add a new lettered version to an existing item. Letter is auto-assigned
  // as the next available a-z if not provided.
  const createVersion = useCallback(async (itemCode, input) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to create a version.' };
      }
      // Find next free version_letter for this item_code
      const used = new Set(
        versions.filter((v) => v.itemCode === itemCode).map((v) => v.versionLetter).filter(Boolean)
      );
      const letter = input.versionLetter || 'abcdefghijklmnopqrstuvwxyz'.split('').find((l) => !used.has(l));
      if (!letter) {
        return { success: false, error: 'No version letters left (a-z exhausted).' };
      }

      const partsError = validateDescriptionParts(input);
      if (partsError) return { success: false, error: partsError };
      const payload = {
        item_code: itemCode,
        version_letter: letter,
        ...descriptionPayload(input),
        long_spec_general: input.longSpecGeneral?.trim() || null,
        long_spec_products: input.longSpecProducts?.trim() || null,
        long_spec_execution: input.longSpecExecution?.trim() || null,
        unit: canonicalUnit(input.unit),
        measurement_ref: input.measurementRef?.trim() || null,
        change_reason: input.changeReason?.trim() || null,
        // The editor passes 'deprecated' (archived) when the item already has an
        // active version, so a new version waits for the curator to press Active.
        status: input.status || 'active',
      };
      const { data, error: insertErr } = await supabase
        .schema('boq')
        .from('item_versions')
        .insert(payload)
        .select(VERSION_COLUMNS)
        .single();
      if (insertErr) throw insertErr;

      // Inherit the item's canonical brio sub-trade so a new version can't drift.
      const inheritedNodeId = brioSubtradeNodeByItem.get(itemCode);
      if (inheritedNodeId) {
        await supabase
          .schema('boq')
          .from('item_version_classifications')
          .upsert(
            { item_version_id: data.id, classification_node_id: inheritedNodeId },
            { onConflict: 'item_version_id,classification_node_id', ignoreDuplicates: true }
          );
      }

      setVersions((prev) => [...prev, mapVersion(data)].sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
      return { success: true, version: mapVersion(data) };
    } catch (err) {
      console.error('[useBoqLibrary] createVersion failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'item version' }) };
    }
  }, [versions, brioSubtradeNodeByItem]);

  // Create a DRAFT candidate version under an existing item, capturing an
  // imported wording for later reconciliation. The next version letter is
  // assigned atomically server-side so a batch import can't collide.
  // Delete one version through boq.delete_item_version(), which refuses the
  // item's last version, an active one, and any version a project line, rate,
  // issued estimate or FF&E row points at, naming which. The modal only offers
  // it on versions made with + New version while it is open.
  const deleteVersion = useCallback(async (versionId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to delete a version.' };
      }
      const { error: rpcErr } = await supabase.schema('boq').rpc('delete_item_version', { p_version_id: versionId });
      if (rpcErr) throw rpcErr;
      setVersions((prev) => prev.filter((v) => v.id !== versionId));
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] deleteVersion failed', err);
      // The function raises sentences meant for a human; show them as they are.
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'delete', entity: 'item version' }) };
    }
  }, []);

  const createCandidateVersion = useCallback(async (itemCode, input) => {
    try {
      const { data, error: rpcErr } = await supabase
        .schema('boq')
        .rpc('create_candidate_version', {
          p_item_code: itemCode,
          p_short_description: input.shortDescription?.trim() || 'Imported spec',
          p_unit: canonicalUnit(input.unit),
          p_source_document_id: input.sourceDocumentId || null,
          p_change_reason: input.changeReason?.trim() || null,
        });
      if (rpcErr) throw rpcErr;
      const mapped = mapVersion(data);
      setVersions((prev) => [...prev, mapped].sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
      return { success: true, version: mapped };
    } catch (err) {
      console.error('[useBoqLibrary] createCandidateVersion failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'candidate version' }) };
    }
  }, []);

  // Fetch lazily: when mounted in a shared/cached context the caller flips
  // `enabled` true the first time the data is actually needed, then keeps the
  // hook alive so the (heavy) dataset is loaded at most once.
  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    supabase.schema('boq').rpc('is_admin_or_supervisor').then(({ data, error: rpcErr }) => {
      if (!cancelled) setCanWrite(!rpcErr && data === true);
    });
    // Absent before its migration: reads as false, so no Delete shows.
    supabase.schema('boq').rpc('can_delete_item_version').then(({ data, error: rpcErr }) => {
      if (!cancelled) setCanDeleteVersion(!rpcErr && data === true);
    });
    return () => { cancelled = true; };
  }, [enabled]);
  useEffect(() => { versionsRef.current = versions; }, [versions]);

  // Lazily load the heavy detail (long-form specs + rate observations) for a
  // set of item codes — typically the items of one trade group as the user
  // selects it. Items already loaded are skipped, so this is safe to call on
  // every selection. Keeps a stable identity (reads versions via a ref) so
  // callers can use it as an effect dependency without re-running.
  const loadGroupDetail = useCallback(async (itemCodes) => {
    const codes = [...new Set(itemCodes || [])].filter((c) => c && !detailLoadedRef.current.has(c));
    if (codes.length === 0) return { success: true };
    // Mark loaded up front so concurrent selections don't double-fetch.
    codes.forEach((c) => detailLoadedRef.current.add(c));
    setDetailLoading(true);
    try {
      const allVersions = versionsRef.current;
      const versionIds = allVersions.filter((v) => codes.includes(v.itemCode)).map((v) => v.id);
      const codeByVersionId = new Map(allVersions.map((v) => [v.id, v.itemCode]));

      const [specRes, rateRes] = await Promise.all([
        supabase.schema('boq').from('item_versions').select(VERSION_SPEC_COLUMNS).in('item_code', codes),
        versionIds.length
          ? supabase.schema('boq').from('rates')
              .select('item_version_id, rate_quoted, rate_billed, rate_date')
              .in('item_version_id', versionIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (specRes.error) throw specRes.error;
      if (rateRes.error) throw rateRes.error;

      const specById = new Map((specRes.data || []).map((r) => [r.id, r]));
      setVersions((prev) => prev.map((v) => {
        const s = specById.get(v.id);
        return s ? {
          ...v,
          longSpecGeneral: s.long_spec_general,
          longSpecProducts: s.long_spec_products,
          longSpecExecution: s.long_spec_execution,
        } : v;
      }));

      // Aggregate rate observations per item: count + most-recent value.
      const agg = new Map();
      (rateRes.data || []).forEach((r) => {
        const code = codeByVersionId.get(r.item_version_id);
        if (!code) return;
        const val = r.rate_billed ?? r.rate_quoted;
        const cur = agg.get(code) || { count: 0, latestDate: '', latestVal: null };
        cur.count += 1;
        if (val != null && (r.rate_date || '') >= cur.latestDate) {
          cur.latestDate = r.rate_date || '';
          cur.latestVal = Number(val);
          // The row reads its wording and unit from this version, so the
          // figure and the description shown beside it describe the same thing.
          cur.latestVersionId = r.item_version_id;
        }
        agg.set(code, cur);
      });
      setRateAggByItem((prev) => {
        const next = new Map(prev);
        // Seed every requested code so the UI can tell "loaded, no rates" from
        // "not loaded yet".
        codes.forEach((c) => { if (!next.has(c)) next.set(c, { count: 0, latestDate: '', latestVal: null }); });
        agg.forEach((v, k) => next.set(k, v));
        return next;
      });
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] loadGroupDetail failed', err);
      // Allow a retry on failure.
      codes.forEach((c) => detailLoadedRef.current.delete(c));
      return { success: false, error: formatSupabaseError(err, { action: 'load', entity: 'library detail' }) };
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Reconcile an item's variants into one canonical version: create a new
  // active version from the composed fields, re-point every rate that pointed
  // at a folded variant to the canonical, then deprecate the folded variants.
  // Rate provenance (source doc / vendor / date) is untouched — only the
  // item_version pointer moves — so the canonical accumulates the full history.
  const reconcileItem = useCallback(async ({ itemCode, canonical, foldVersionIds }) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to reconcile.' };
      }
      if (!canonical.shortDescription?.trim()) return { success: false, error: 'Canonical short description is required.' };
      if (!canonical.unit?.trim()) return { success: false, error: 'Canonical unit is required.' };

      const used = new Set(versions.filter((v) => v.itemCode === itemCode).map((v) => v.versionLetter).filter(Boolean));
      const letter = 'abcdefghijklmnopqrstuvwxyz'.split('').find((l) => !used.has(l));
      if (!letter) return { success: false, error: 'No version letters left (a-z exhausted).' };

      const payload = {
        item_code: itemCode,
        version_letter: letter,
        short_description: canonical.shortDescription.trim(),
        long_spec_general: canonical.longSpecGeneral?.trim() || null,
        long_spec_products: canonical.longSpecProducts?.trim() || null,
        long_spec_execution: canonical.longSpecExecution?.trim() || null,
        unit: canonicalUnit(canonical.unit),
        measurement_ref: canonical.measurementRef?.trim() || null,
        change_reason: canonical.changeReason?.trim() || 'Reconciled canonical version',
        status: 'active',
      };
      const { data: canonRow, error: insErr } = await supabase
        .schema('boq').from('item_versions').insert(payload).select(VERSION_COLUMNS).single();
      if (insErr) throw insErr;
      const canonicalId = canonRow.id;

      const fold = (foldVersionIds || []).filter((id) => id !== canonicalId);
      if (fold.length) {
        const { error: reErr } = await supabase
          .schema('boq').from('rates').update({ item_version_id: canonicalId }).in('item_version_id', fold);
        if (reErr) throw reErr;
        const { error: depErr } = await supabase
          .schema('boq').from('item_versions').update({ status: 'deprecated' }).in('id', fold);
        if (depErr) throw depErr;
      }

      await fetchAll();
      return { success: true, version: mapVersion(canonRow), foldedCount: fold.length };
    } catch (err) {
      console.error('[useBoqLibrary] reconcileItem failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'reconcile', entity: 'item' }) };
    }
  }, [versions, fetchAll]);

  // Edit a version's content fields in place. Used for draft/unreferenced
  // versions; callers route referenced active versions to createVersion instead
  // (so historical projects keep their snapshot). Pass only the fields to change.
  const updateVersion = useCallback(async (versionId, patch) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to edit a version.' };
      }
      const payload = {};
      // The description is edited as its five parts. A patch carrying any of
      // them sends all five, so a part cleared in the form is cleared here.
      if (touchesDescriptionParts(patch)) {
        const partsError = validateDescriptionParts(patch);
        if (partsError) return { success: false, error: partsError };
        Object.assign(payload, descriptionPayload(patch));
      } else if (patch.shortDescription !== undefined) {
        if (!patch.shortDescription.trim()) return { success: false, error: 'Short description is required.' };
        payload.short_description = patch.shortDescription.trim();
      }
      if (patch.unit !== undefined) {
        if (!patch.unit.trim()) return { success: false, error: 'Unit is required.' };
        payload.unit = canonicalUnit(patch.unit);
      }
      if (patch.measurementRef !== undefined) payload.measurement_ref = patch.measurementRef?.trim() || null;
      if (patch.longSpecGeneral !== undefined) payload.long_spec_general = patch.longSpecGeneral?.trim() || null;
      if (patch.longSpecProducts !== undefined) payload.long_spec_products = patch.longSpecProducts?.trim() || null;
      if (patch.longSpecExecution !== undefined) payload.long_spec_execution = patch.longSpecExecution?.trim() || null;
      if (patch.changeReason !== undefined) payload.change_reason = patch.changeReason?.trim() || null;
      if (patch.status !== undefined) payload.status = patch.status;
      if (Object.keys(payload).length === 0) return { success: true };

      const { data, error: updErr } = await supabase
        .schema('boq').from('item_versions').update(payload).eq('id', versionId).select(VERSION_COLUMNS).single();
      if (updErr) throw updErr;
      setVersions((prev) => prev.map((v) => (v.id === versionId ? mapVersion(data) : v)).sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
      return { success: true, version: mapVersion(data) };
    } catch (err) {
      console.error('[useBoqLibrary] updateVersion failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'item version' }) };
    }
  }, []);

  // Set an item's status (active / deprecated / draft).
  const setItemStatus = useCallback(async (itemCode, status) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to change item status.' };
      }
      const { data, error: updErr } = await supabase
        .schema('boq').from('items').update({ status }).eq('item_code', itemCode).select(ITEM_COLUMNS).single();
      if (updErr) throw updErr;
      setItems((prev) => prev.map((i) => (i.itemCode === itemCode ? mapItem(data) : i)));
      return { success: true, item: mapItem(data) };
    } catch (err) {
      console.error('[useBoqLibrary] setItemStatus failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'item' }) };
    }
  }, []);

  // Set (or clear) an item's trade-group classification. Trade group is a
  // per-item concept stored per version, so we apply it to *every* version of
  // the item: drop any existing trade_group tags, then tag each version with
  // the chosen node. Pass nodeId = null to clear.
  const setItemTradeGroup = useCallback(async (itemCode, nodeId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to tag items.' };
      }
      const versionIds = versions.filter((v) => v.itemCode === itemCode).map((v) => v.id);
      if (!versionIds.length) return { success: false, error: 'This item has no versions to tag.' };
      const tradeNodeIds = tradeGroups.map((n) => n.id);

      // Clear existing trade_group tags on this item's versions.
      if (tradeNodeIds.length) {
        const { error: delErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .delete()
          .in('item_version_id', versionIds)
          .in('classification_node_id', tradeNodeIds);
        if (delErr) throw delErr;
      }
      // Tag every version with the chosen node (skip when clearing).
      if (nodeId) {
        const rows = versionIds.map((id) => ({ item_version_id: id, classification_node_id: nodeId }));
        const { error: insErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .upsert(rows, { onConflict: 'item_version_id,classification_node_id', ignoreDuplicates: true });
        if (insErr) throw insErr;
      }

      const node = tradeGroups.find((n) => n.id === nodeId) || null;
      setTradeGroupByItem((prev) => {
        const next = new Map(prev);
        if (node) next.set(itemCode, node); else next.delete(itemCode);
        return next;
      });
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] setItemTradeGroup failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'classification' }) };
    }
  }, [versions, tradeGroups]);

  // Set (or clear) an item's canonical brio sub-trade. Like the trade_group
  // helper, this applies to every version of the item: drop existing brio
  // sub-trade tags, then tag each version with the chosen sub-trade node.
  // Pass nodeId = null to clear.
  const setItemBrioSubtrade = useCallback(async (itemCode, nodeId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to classify items.' };
      }
      const versionIds = versions.filter((v) => v.itemCode === itemCode).map((v) => v.id);
      if (!versionIds.length) return { success: false, error: 'This item has no versions to classify.' };
      const subtradeNodeIds = brioSubtrades.map((n) => n.id);

      if (subtradeNodeIds.length) {
        const { error: delErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .delete()
          .in('item_version_id', versionIds)
          .in('classification_node_id', subtradeNodeIds);
        if (delErr) throw delErr;
      }
      if (nodeId) {
        const rows = versionIds.map((id) => ({ item_version_id: id, classification_node_id: nodeId }));
        const { error: insErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .upsert(rows, { onConflict: 'item_version_id,classification_node_id', ignoreDuplicates: true });
        if (insErr) throw insErr;
      }

      const sub = brioSubtrades.find((n) => n.id === nodeId) || null;
      setBrioSubtradeNodeByItem((prev) => { const next = new Map(prev); if (sub) next.set(itemCode, sub.id); else next.delete(itemCode); return next; });
      setSubtradeByItem((prev) => { const next = new Map(prev); if (sub) next.set(itemCode, sub.label); else next.delete(itemCode); return next; });
      setChapterByItem((prev) => { const next = new Map(prev); if (sub) next.set(itemCode, sub.chapterLabel); else next.delete(itemCode); return next; });
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] setItemBrioSubtrade failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'classification' }) };
    }
  }, [versions, brioSubtrades]);

  // Set (or clear) an item's trade type. Same shape as the brio sub-trade
  // helper: the tag lives per version, so it applies to every version of the
  // item. Pass nodeId = null to clear.
  const setItemTradeType = useCallback(async (itemCode, nodeId) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to classify items.' };
      }
      const versionIds = versions.filter((v) => v.itemCode === itemCode).map((v) => v.id);
      if (!versionIds.length) return { success: false, error: 'This item has no versions to classify.' };
      const typeNodeIds = tradeTypes.map((n) => n.id);

      if (typeNodeIds.length) {
        const { error: delErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .delete()
          .in('item_version_id', versionIds)
          .in('classification_node_id', typeNodeIds);
        if (delErr) throw delErr;
      }
      if (nodeId) {
        const rows = versionIds.map((id) => ({ item_version_id: id, classification_node_id: nodeId }));
        const { error: insErr } = await supabase
          .schema('boq').from('item_version_classifications')
          .upsert(rows, { onConflict: 'item_version_id,classification_node_id', ignoreDuplicates: true });
        if (insErr) throw insErr;
      }

      setTradeTypeNodeByItem((prev) => {
        const next = new Map(prev);
        if (nodeId) next.set(itemCode, nodeId); else next.delete(itemCode);
        return next;
      });
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibrary] setItemTradeType failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'trade type' }) };
    }
  }, [versions, tradeTypes]);

  // Change an existing item's trade group, sub-trade and trade type together,
  // renumbering it when the current code no longer fits. One RPC, one
  // transaction: boq.reclassify_library_item() swaps the tags on every version
  // and renames the item, and re-checks that the new code agrees with the
  // classification rather than trusting this side. The old code is recorded
  // in boq.item_code_renames. Live project BOQs and issued bill snapshots both
  // show the new code (item_versions_sync_bill_codes_trg); library archives
  // keep the old one. Leaving Purge always renumbers, since an OO.OO code
  // never fits a real classification.
  const reclassifyItem = useCallback(async (itemCode, sel) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to reclassify items.' };
      }
      const proposal = proposeItemCode(itemCode, sel);
      if (proposal.error) return { success: false, error: proposal.error };

      const { data: newCode, error: rpcErr } = await supabase.schema('boq').rpc('reclassify_library_item', {
        p_item_code: itemCode,
        p_trade_group_id: sel.tradeGroupNodeId,
        p_subtrade_id: sel.brioSubtradeNodeId,
        p_trade_type_id: sel.tradeTypeNodeId || null,
        p_new_code: proposal.code,
      });
      if (rpcErr) throw rpcErr;

      const code = newCode || proposal.code;
      const renamed = code !== itemCode;
      const tg = tradeGroups.find((n) => n.id === sel.tradeGroupNodeId) || null;
      const sub = brioSubtrades.find((n) => n.id === sel.brioSubtradeNodeId) || null;

      // Patch state rather than re-reading the whole library: move every
      // per-item entry to the new key, then write the new classification.
      const rekey = (setter, value) => setter((prev) => {
        const next = new Map(prev);
        const carried = next.get(itemCode);
        next.delete(itemCode);
        const v = value === undefined ? carried : value;
        if (v != null) next.set(code, v);
        return next;
      });
      if (renamed) {
        const parsed = parseItemCode(code);
        setItems((prev) => prev
          .map((i) => (i.itemCode === itemCode
            ? { ...i, itemCode: code, trade: parsed.trade, subtrade: parsed.subtrade, sequence: parsed.sequence }
            : i))
          .sort((a, b) => a.itemCode.localeCompare(b.itemCode)));
        setVersions((prev) => prev
          .map((v) => (v.itemCode === itemCode
            ? { ...v, itemCode: code, fullCode: v.versionLetter ? `${code}.${v.versionLetter}` : code }
            : v))
          .sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
        rekey(setRateAggByItem);
        if (detailLoadedRef.current.delete(itemCode)) detailLoadedRef.current.add(code);
      }
      rekey(setTradeGroupByItem, tg);
      rekey(setBrioSubtradeNodeByItem, sub ? sub.id : null);
      rekey(setSubtradeByItem, sub ? sub.label : null);
      rekey(setChapterByItem, sub ? sub.chapterLabel : null);
      rekey(setTradeTypeNodeByItem, sel.tradeTypeNodeId || null);
      return { success: true, itemCode: code, renamed };
    } catch (err) {
      console.error('[useBoqLibrary] reclassifyItem failed', err);
      // The function raises sentences meant for a human; show them as they are.
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'classification' }) };
    }
  }, [proposeItemCode, tradeGroups, brioSubtrades]);

  // Move items into Purge, in the order given. boq.purge_library_items()
  // renumbers each to the lowest free OO.OO code and drops its trade group,
  // chapter / sub-trade and trade type tags, in one transaction. Items already
  // in Purge are skipped. State is patched from the returned renames rather
  // than re-reading the whole library.
  const purgeItems = useCallback(async (itemCodes) => {
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      if (sessionErr || !sessionData?.session) {
        return { success: false, error: 'Please sign in to move items to Purge.' };
      }
      const codes = [...new Set(itemCodes)].filter((c) => !isPurgeCode(c)).sort();
      if (!codes.length) return { success: true, moved: [] };
      const { data, error: rpcErr } = await supabase.schema('boq').rpc('purge_library_items', { p_item_codes: codes });
      if (rpcErr) throw rpcErr;
      const moved = (data || []).map((row) => ({ from: row.from_code, to: row.to_code }));
      const renames = new Map(moved.map((m) => [m.from, m.to]));
      if (renames.size) {
        setItems((prev) => prev
          .map((i) => {
            const to = renames.get(i.itemCode);
            if (!to) return i;
            const parsed = parseItemCode(to);
            return { ...i, itemCode: to, trade: parsed.trade, subtrade: parsed.subtrade, sequence: parsed.sequence };
          })
          .sort((a, b) => a.itemCode.localeCompare(b.itemCode)));
        setVersions((prev) => prev
          .map((v) => {
            const to = renames.get(v.itemCode);
            return to ? { ...v, itemCode: to, fullCode: v.versionLetter ? `${to}.${v.versionLetter}` : to } : v;
          })
          .sort((a, b) => a.fullCode.localeCompare(b.fullCode)));
        // Carried across: rates and loaded detail. Dropped: every tag map.
        const rekey = (setter, keep) => setter((prev) => {
          const next = new Map(prev);
          renames.forEach((to, from) => {
            const carried = next.get(from);
            next.delete(from);
            if (keep && carried != null) next.set(to, carried);
          });
          return next;
        });
        rekey(setRateAggByItem, true);
        rekey(setTradeGroupByItem, false);
        rekey(setBrioSubtradeNodeByItem, false);
        rekey(setSubtradeByItem, false);
        rekey(setChapterByItem, false);
        rekey(setTradeTypeNodeByItem, false);
        renames.forEach((to, from) => {
          if (detailLoadedRef.current.delete(from)) detailLoadedRef.current.add(to);
        });
      }
      return { success: true, moved };
    } catch (err) {
      console.error('[useBoqLibrary] purgeItems failed', err);
      return { success: false, error: err?.message || formatSupabaseError(err, { action: 'update', entity: 'library items' }) };
    }
  }, []);

  // Every recorded change to an item: the item row, its versions, their
  // classification tags and their rates, newest first, across every code it has
  // carried. Read from boq.audit_log through boq.item_activity(), which only
  // admins and supervisors can see.
  const fetchItemActivity = useCallback(async (itemCode) => {
    try {
      const { data, error: rpcErr } = await supabase.schema('boq').rpc('item_activity', { p_item_code: itemCode });
      if (rpcErr) throw rpcErr;
      return {
        success: true,
        rows: (data || []).map((r) => ({
          id: r.id,
          changedAt: r.changed_at,
          table: (r.table_name || '').replace(/^boq\./, ''),
          action: r.action,
          actorName: r.actor_name,
          before: r.before,
          after: r.after,
          versionCode: r.version_code,
          nodeLabel: r.node_label,
          taxonomyCode: r.taxonomy_code,
        })),
      };
    } catch (err) {
      console.error('[useBoqLibrary] fetchItemActivity failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'load', entity: 'item history' }) };
    }
  }, []);

  return {
    items,
    versions,
    tradeGroups,
    tradeGroupByItem,
    chapterByItem,
    subtradeByItem,
    brioSubtrades,
    brioSubtradeNodeByItem,
    tradeTypes,
    tradeTypeNodeByItem,
    subtradeLinks,
    rateAggByItem,
    modifiedAtByItem,
    canWrite,
    canDeleteVersion,
    deleteVersion,
    loadGroupDetail,
    detailLoading,
    loading,
    error,
    refresh: fetchAll,
    refreshPickers,
    createItemWithFirstVersion,
    duplicateItem,
    setActiveVersion,
    mergeItems,
    unmergeItems,
    loadSplitCandidates,
    splitItem,
    combineVersions,
    deleteItem,
    createVersion,
    createCandidateVersion,
    reconcileItem,
    setItemTradeGroup,
    setItemBrioSubtrade,
    setItemTradeType,
    nextItemCode,
    nextDuplicateCode,
    proposeItemCode,
    reclassifyItem,
    purgeItems,
    fetchItemActivity,
    updateVersion,
    setItemStatus,
  };
};
